/**
 * The guide's brain: which step we are on, getting to the right screen, finding
 * the anchor, and remembering that it has been seen.
 *
 * Deliberately separate from the overlay, which only draws what this decides.
 *
 * Persistence lives on the user document, not in localStorage: "has seen the
 * guide" has to survive a reinstall and follow the account across devices, and
 * the skip reminder is sent by a scheduled Cloud Function that can only read
 * Firestore.
 *
 *   guideCompletedAt   finished it
 *   guideSkippedAt     skipped it
 *   guideReminderDueAt when the one-and-only reminder becomes due (skip only)
 *   guideVersion       which GUIDE_STEPS revision was seen
 *
 * Either of the first two fields means "do not show automatically again".
 * Replay from Settings clears them, which is what makes Replay work for
 * existing accounts that never qualified in the first place.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { getDbSafe } from '../services/firebaseConfig';
import { useUserData } from '../components/UserDataContext';
import { POPUP_PRIORITY, usePopupSlot } from '../contexts/PopupQueueContext';
import { SafeStorage } from '../utils/safeStorage';
import { PATHS } from '../router/paths';
import { useDiscoverContext } from '../contexts/DiscoverContext';
import { ANCHOR_TIMEOUT_MS, GUIDE_STEPS, type GuideSurface } from './guideSteps';

/**
 * Bump when the steps change materially and everyone should see it again.
 * Not read on the automatic path (either completion field suppresses that
 * regardless), so raising it does not re-tour the whole user base by surprise.
 */
export const GUIDE_VERSION = 1;

/**
 * How long to keep re-measuring an anchor after finding it, so the spotlight
 * follows a screen transition to where the element actually comes to rest.
 */
const ANCHOR_SETTLE_MS = 800;

/**
 * How often to keep checking for an anchor AFTER its step has already been
 * shown without one. Slower than the initial hunt because nobody is waiting on
 * it: the step is already readable, this only upgrades it to a spotlight.
 */
const ANCHOR_LATE_POLL_MS = 400;

/** How often to look for an anchor that is not on screen yet. */
const ANCHOR_POLL_MS = 100;
/** Grace period after navigating before the anchor hunt starts. */
const NAV_SETTLE_MS = 250;
/**
 * Routes the tour must never start on. These are the onboarding and auth screens:
 * starting there would navigate the user out of a flow they are in the middle of.
 */
const SETUP_ROUTES = [
  PATHS.PROFILE_SETUP, PATHS.ONBOARDING, PATHS.LOGIN, PATHS.SIGNUP,
];

/**
 * How long to defer to the PIN prompt and the rules modal before arming anyway.
 * Long enough for a normal onboarding to finish those two, short enough that a
 * stuck flag costs one delay rather than the whole feature.
 */
const ARM_PATIENCE_MS = 20000;

/** How long after a skip the single reminder becomes due. */
const REMINDER_DELAY_MS = 24 * 60 * 60 * 1000;

export interface GuideApi {
  /** True while the tour is running, INCLUDING while paused behind a popup. */
  active: boolean;
  /**
   * True only while the tour should actually be drawn: running AND holding the
   * popup queue slot. False while something more important is up.
   */
  visible: boolean;
  /** Index into GUIDE_STEPS, or -1 when inactive. */
  stepIndex: number;
  /** Measured rect of the current anchor, or null for an untargeted step. */
  anchorRect: DOMRect | null;
  /** Waiting for the current step's anchor to turn up. */
  resolving: boolean;
  /**
   * The screen is changing and nothing should be drawn over it yet.
   *
   * Distinct from `resolving`, which is also true while waiting for an anchor on
   * a screen that is already up — there the bubble should stay put and travel.
   * This is only the window between leaving one screen and the next one being
   * ready, when there is nothing worth pointing at.
   */
  transitioning: boolean;
  start: () => void;
  next: () => void;
  back: () => void;
  skip: () => void;
}

const GuideContext = createContext<GuideApi | null>(null);

export function GuideProvider({ children }: { children: ReactNode }) {
  // Read straight from UserDataContext rather than taking a uid prop: the only
  // component able to pass one in is above the Router, and the provider has to
  // sit inside it to navigate.
  const { firebaseUser, userProfile, profileSettled } = useUserData();
  // The swipe deck is context state rather than a route, so it is driven
  // directly. GuideProvider sits inside DiscoverProvider in AppRouter.
  const { setSwipeModeActive } = useDiscoverContext();
  const uid = firebaseUser?.uid ?? null;
  const profile = userProfile as any;
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [resolving, setResolving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  /** Qualifies for the automatic run and is waiting for its turn. */
  const [armed, setArmed] = useState(false);

  // The claim covers `armed || active`, not just the moment it starts.
  //
  // Claiming only while waiting to start was a real bug: the tour took the slot,
  // began, dropped its claim on the same tick, and the queue handed the slot to
  // the PIN prompt — which then rendered ON TOP of a tour that was still running,
  // because the overlay drew on `active`. Two popups on screen at once, which is
  // the exact thing the queue exists to prevent.
  //
  // Holding the claim for the whole run also gives the right behaviour when
  // something MORE important arrives midway: SECURITY outranks GUIDE, so the
  // queue takes the slot away, the tour goes invisible but keeps its place, and
  // it comes back on the same step once the PIN prompt is dealt with. Paused,
  // not cancelled, and never underneath.
  const slot = usePopupSlot('guide', POPUP_PRIORITY.GUIDE, armed || active);
  const visible = active && slot;

  // Advancing is driven from two places (the buttons and the anchor timeout), so
  // the timers need a stable way to move the tour on without being recreated.
  const advanceRef = useRef<(delta: number) => void>(() => {});
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const settleFrameRef = useRef<number>(0);
  /** Has this step already been shown without waiting any longer for its anchor? */
  const releasedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (settleFrameRef.current) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = 0;
    }
  }, []);

  // ── Persistence ────────────────────────────────────────────────────────────
  const writeUser = useCallback(async (patch: Record<string, unknown>) => {
    if (!uid) return;
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'users', uid), patch);
    } catch (e) {
      // A failed write must not strand the user inside the overlay. Worst case
      // they see the guide once more.
      console.warn('[Guide] Could not persist guide state:', e);
    }
  }, [uid]);

  // ── Surfaces: modals and sub-screens a step needs open ────────────────────
  //
  // Every one of these is local component state somewhere, not a route, so the
  // guide asks for it by event and the owning screen obeys. Events rather than
  // lifted state because that is already how this app talks to itself
  // (navigateToScreen, openCreateYapModal and the rest), and lifting five
  // unrelated pieces of state into a shared context just so a tour can reach
  // them would be a much larger change to screens that are otherwise fine.
  //
  // applySurface is the whole mechanism. It is called with what the current step
  // wants and closes whatever else is open, so it does not matter whether the
  // reader arrived by Next, by Back, or by the tour starting mid-way: the result
  // is the same. Skip and finish call it with null.
  const currentSurfaceRef = useRef<GuideSurface | null>(null);

  const applySurface = useCallback((next: GuideSurface | null) => {
    const prev = currentSurfaceRef.current;
    if (prev === next) return;
    currentSurfaceRef.current = next;

    // Close what is open first, so two surfaces are never up at once.
    if (prev === 'postModal') window.dispatchEvent(new CustomEvent('guide:closePostModal'));
    if (prev === 'shareProfile') window.dispatchEvent(new CustomEvent('guide:closeShareProfile'));
    if (prev === 'swipe') setSwipeModeActive(false);
    if (prev === 'createRoom') window.dispatchEvent(new CustomEvent('guide:closeCreateRoom'));

    if (next === 'postModal') window.dispatchEvent(new CustomEvent('openCreateYapModal'));
    if (next === 'shareProfile') window.dispatchEvent(new CustomEvent('guide:openShareProfile'));
    if (next === 'swipe') setSwipeModeActive(true);
    if (next === 'chatRooms') window.dispatchEvent(new CustomEvent('guide:chatSection', { detail: 'rooms' }));
    if (next === 'chatMessages') window.dispatchEvent(new CustomEvent('guide:chatSection', { detail: 'messages' }));
    if (next === 'createRoom') {
      // The modal lives inside the Rooms section, so make sure that section is
      // the one showing before asking for it. Harmless when it already is.
      window.dispatchEvent(new CustomEvent('guide:chatSection', { detail: 'rooms' }));
      window.dispatchEvent(new CustomEvent('guide:openCreateRoom'));
    }
  }, [setSwipeModeActive]);

  /** Remove the guide row(s) from the user's own notification list. */
  const clearGuideNotifications = useCallback(async () => {
    if (!uid) return;
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'notifications'),
        where('userId', '==', uid),
        where('type', '==', 'GUIDE_REPLAY'),
      ));
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
    } catch (e) {
      console.warn('[Guide] Could not clear the guide notification:', e);
    }
  }, [uid]);

  // ── Finishing ──────────────────────────────────────────────────────────────
  const finish = useCallback(() => {
    clearTimers();
    // The closing step ends with the composer open on purpose, so it opts out of
    // the teardown. Every other step closes whatever it opened.
    const last = GUIDE_STEPS[GUIDE_STEPS.length - 1];
    if (!last?.keepSurfaceOnFinish) applySurface(null);
    else currentSurfaceRef.current = last.surface ?? null;
    setArmed(false);
    setActive(false);
    setStepIndex(-1);
    setAnchorRect(null);
    setResolving(false);
    SafeStorage.removeItem('pending_guide');
    void writeUser({
      guideCompletedAt: serverTimestamp(),
      guideVersion: GUIDE_VERSION,
      // A completed tour cancels any reminder a previous skip had queued.
      guideReminderDueAt: null,
    });
    // Clear the access row too. Once the tour is finished, an invitation to take
    // it sitting in the notification list is just stale.
    void clearGuideNotifications();
  }, [clearTimers, writeUser, clearGuideNotifications]);

  /**
   * Drop the "Want a quick tour?" row into the user's OWN notification list.
   *
   * Written here rather than left to the scheduled sweep because an unfinished
   * guide has to be reachable from Notifications straight away, not up to
   * fifteen minutes later. `silent: true` keeps it to an in-app row with no
   * push: it is a way back in, not an interruption.
   *
   * Self-addressed, which is the only reason the rules permit a client to write
   * a GUIDE_REPLAY or to set silent at all — see firestore.rules.
   */
  const writeGuideNotification = useCallback(async () => {
    if (!uid) return;
    try {
      const db = await getDbSafe();
      // One row, ever. Skip() is not the only writer any more — the self-heal
      // effect below writes one for any unfinished guide — so an unconditional
      // add() here produced a second identical invitation for anyone whose row
      // already existed, and a third if Skip was tapped twice. Checking first
      // makes every caller safe to call at any time.
      const existing = await getDocs(query(
        collection(db, 'notifications'),
        where('userId', '==', uid),
        where('type', '==', 'GUIDE_REPLAY'),
      ));
      if (!existing.empty) return;
      await addDoc(collection(db, 'notifications'), {
        userId: uid,
        type: 'GUIDE_REPLAY',
        title: 'Want a quick tour of AfroYap?',
        message: 'It takes about 5 minutes and shows you around.',
        body: 'It takes about 5 minutes and shows you around.',
        read: false,
        senderId: '',
        silent: true,
        deepLink: 'afroyap://guide',
        threadId: 'guide',
        collapseKey: `guide-${uid}`,
        timestamp: serverTimestamp(),
      });
    } catch (e) {
      console.warn('[Guide] Could not write the guide notification:', e);
    }
  }, [uid]);

  const skip = useCallback(() => {
    clearTimers();
    // Skipping always leaves a clean screen, from any step, including the last.
    applySurface(null);
    setArmed(false);
    setActive(false);
    setStepIndex(-1);
    setAnchorRect(null);
    setResolving(false);
    SafeStorage.removeItem('pending_guide');
    void writeUser({
      guideSkippedAt: serverTimestamp(),
      guideVersion: GUIDE_VERSION,
      // The row above is the access point and exists from now on. This is the
      // separate day-later nudge, which re-surfaces that same row rather than
      // adding a second one. The sweep clears the field when it fires, which is
      // what keeps it to exactly one.
      guideReminderDueAt: new Date(Date.now() + REMINDER_DELAY_MS),
    });
    void writeGuideNotification();
  }, [clearTimers, writeUser, writeGuideNotification, applySurface]);

  // ── Moving between steps ───────────────────────────────────────────────────
  const advance = useCallback((delta: number) => {
    clearTimers();
    setStepIndex(prev => {
      const target = prev + delta;
      if (target >= GUIDE_STEPS.length) {
        finish();
        return -1;
      }
      // Back from the first step is a no-op rather than an exit: leaving the
      // tour is Skip's job, and a stray Back should not look like a crash.
      if (target < 0) return prev;
      return target;
    });
  }, [clearTimers, finish]);

  advanceRef.current = advance;

  const start = useCallback(() => {
    clearTimers();
    setArmed(false);
    setActive(true);
    setStepIndex(0);
    // Recorded so the access row below can tell "began the tour and never
    // finished it" apart from "has not reached the tour yet". Those two states
    // are otherwise identical on the profile.
    void writeUser({ guideStartedAt: serverTimestamp() });
  }, [clearTimers, writeUser]);

  // Does this account qualify for the automatic run, and is it time yet?
  //
  // `pending_guide` marks "just finished onboarding", exactly as
  // `pending_guidelines` does for the rules modal — it is not a "has never seen
  // it" flag, so existing accounts are never interrupted and reach the tour
  // through Replay in Settings instead.
  //
  // The tour is LAST, and that is enforced here rather than left to the queue
  // alone. Priority ordering only decides who wins when two popups want the slot
  // at the same instant, and these do not arrive at the same instant: the
  // guidelines gate sets its flag on a one-second timer, so for that first second
  // nothing outranked the tour. It took the slot, started, and was then preempted
  // the moment the rules modal appeared — which is what the flashing and
  // pulsating was. Waiting for the earlier steps to actually be finished removes
  // the race instead of narrowing it.
  //
  // The route check is the other half: profileSettled can go true while the user
  // is still on the profile-setup screen, and the tour would then navigate them
  // out of onboarding into the YapTab mid-flow.
  useEffect(() => {
    if (!uid || active || armed) return;

    const startedWaitingAt = Date.now();

    /** Conditions that must ALWAYS hold, however long we have waited. */
    const eligible = () => {
      // Same reason as the row check above: an unloaded profile reads as "never
      // completed", and arming on that would re-run the tour for someone who has
      // already finished it.
      if (!profileSettled || !profile?.id) return false;
      if (SafeStorage.getItem('pending_guide') !== 'true') return false;
      if (profile?.guideCompletedAt || profile?.guideSkippedAt) return false;
      // Still inside onboarding or auth: starting here would navigate the user
      // out of a flow they are in the middle of.
      if (SETUP_ROUTES.some(r => window.location.pathname.startsWith(r))) return false;
      // A session opened by tapping a push is going somewhere specific; a tour
      // over the top of it is noise. Mirrors the guidelines gate.
      try {
        if (sessionStorage.getItem('enteredFromPush_v1') === 'true') return false;
      } catch { /* storage unavailable */ }
      return true;
    };

    /**
     * Would rather wait: the PIN prompt and the rules have not been dealt with.
     *
     * This is a PREFERENCE, not a requirement, and the difference matters. These
     * are localStorage flags cleared by other components, and a flag that never
     * gets cleared — a path that dismisses the PIN prompt without going through
     * its onClose, an interrupted session — would otherwise mean the tour never
     * appears at all. That is exactly what happened when this was a hard gate.
     *
     * So it defers for a while and then goes anyway. The popup queue is still
     * there underneath and still orders by priority, so even when the wait times
     * out the tour cannot draw over the rules modal or the PIN prompt: it just
     * takes its turn after them.
     */
    const wouldRatherWait = () => (
      SafeStorage.getItem('pending_security_setup') === 'true'
      || SafeStorage.getItem('pending_guidelines') === 'true'
    );

    const ready = () => {
      if (!eligible()) return false;
      if (!wouldRatherWait()) return true;
      return Date.now() - startedWaitingAt > ARM_PATIENCE_MS;
    };

    // Polled rather than reactive because the conditions it waits on are
    // localStorage flags cleared by other components, which fire no React update
    // here. Two cheap reads a second, and it stops the moment the tour arms.
    if (ready()) {
      setArmed(true);
      return;
    }
    const iv = setInterval(() => {
      if (ready()) {
        setArmed(true);
        clearInterval(iv);
      }
    }, 700);
    return () => clearInterval(iv);
  }, [uid, active, armed, profileSettled, profile?.id, profile?.guideCompletedAt, profile?.guideSkippedAt]);

  /**
   * An unfinished guide must always be reachable from Notifications.
   *
   * The automatic run is one-shot: arming clears `pending_guide`, so it cannot
   * nag on every launch. But that made "started" and "finished" the same thing
   * as far as recovery went — a tour that was interrupted, preempted by the PIN
   * prompt, or simply left by refreshing the page had consumed its one run,
   * written neither guideCompletedAt nor guideSkippedAt, and created no access
   * row, because the row was only written on an explicit Skip. The account was
   * then stuck: no auto-run, no notification, and Settings the only way back.
   *
   * So the row is ensured here instead, for ANY account whose guide is
   * unfinished once the automatic run has been consumed. It self-heals accounts
   * already in that state rather than only fixing it going forward.
   *
   * Runs once per session and checks for an existing row first, so it cannot
   * pile up duplicates.
   */
  const ensuredRowRef = useRef(false);
  useEffect(() => {
    if (!uid || ensuredRowRef.current) return;
    // NEVER WHILE THE TOUR IS RUNNING. Replay from Settings clears
    // guideCompletedAt and guideSkippedAt and then starts the tour, which left
    // this looking at a profile that says "started, never finished" — so
    // pressing Replay immediately dropped a "Want a quick tour?" row into the
    // reader's notifications while the tour they had just asked for was on
    // screen. The row is for someone who is NOT in the tour.
    if (active) return;
    // WAIT FOR THE PROFILE. Without this the check below reads guideCompletedAt
    // off a profile that has not loaded yet, sees undefined, concludes the guide
    // is unfinished and writes the row — so someone who completed the tour got
    // invited to take it again on their next cold start, every time. "Not loaded"
    // and "not completed" are not the same thing and this is the one place that
    // difference is load-bearing.
    if (!profileSettled || !profile?.id) return;
    // Finished with it one way or the other: skip() writes its own row, finish()
    // deliberately removes it.
    if (profile?.guideCompletedAt || profile?.guideSkippedAt) return;
    // Only for someone who actually BEGAN the tour and did not finish it.
    //
    // This used to key off `pending_guide` being absent, on the reasoning that
    // an absent flag meant the automatic run had been consumed. It also matches
    // the opposite situation: the flag is not set until ProfileSetupScreen
    // COMPLETES, so every brand-new account still in onboarding read as
    // "consumed" and got invited to take a tour it had not been offered yet.
    // guideStartedAt distinguishes the two; nothing else on the profile does.
    if (!profile?.guideStartedAt) return;
    // Belt and braces: never while the reader is still inside setup or auth.
    if (SETUP_ROUTES.some(r => window.location.pathname.startsWith(r))) return;

    ensuredRowRef.current = true;
    void (async () => {
      try {
        const db = await getDbSafe();
        const existing = await getDocs(query(
          collection(db, 'notifications'),
          where('userId', '==', uid),
          where('type', '==', 'GUIDE_REPLAY'),
        ));
        if (!existing.empty) return;
        await writeGuideNotification();
      } catch (e) {
        console.warn('[Guide] Could not ensure the guide notification:', e);
      }
    })();
  }, [uid, active, profileSettled, profile?.id, profile?.guideStartedAt,
      profile?.guideCompletedAt, profile?.guideSkippedAt, writeGuideNotification]);

  // Start once the queue says it is this tour's turn — after the PIN prompt and
  // the community guidelines, before the daily reward.
  useEffect(() => {
    if (!armed || !slot || active) return;
    // `pending_guide` is NOT cleared here. Clearing it on start meant that
    // merely BEGINNING the tour consumed it, so a tour that was interrupted —
    // preempted by the PIN prompt, or left by refreshing the page — had used up
    // its one automatic run without the reader having seen anything. It is
    // cleared by finish() and skip() instead, the two ways a reader actually
    // deals with it, both of which are always one tap away.
    start();
  }, [armed, slot, active, start]);

  // ── Navigate to the step's screen ──────────────────────────────────────────
  const step = active && stepIndex >= 0 ? GUIDE_STEPS[stepIndex] : null;

  // Re-asserted on every route change, not only when the step changes.
  //
  // This used to key on step.id alone, on the reasoning that a user-driven
  // navigation mid-tour should not be fought. But the tour OWNS the screen while
  // it runs, and the route can move under a step without the reader doing
  // anything deliberate: AppRouter replays a pending push route on a cold start,
  // deep links land, ProtectedRoute redirects — and taps are only swallowed
  // while the overlay is actually rendered, which is `active && slot`, so
  // whenever the popup queue hands the slot to something higher priority the
  // tour stays active but invisible and taps pass straight through to the app.
  //
  // In all of those cases the tour previously stayed put and kept painting a
  // cut-out at coordinates measured on the screen it had left, which is the
  // "screen changed, guide still highlighting the wrong thing" symptom. Slow
  // navigation just widens the window.
  //
  // Re-asserting cannot loop: it only navigates when the path differs from the
  // step's, so the moment it matches the effect is inert.
  useEffect(() => {
    if (!step) return;

    if (location.pathname !== step.route) {
      // Close whatever is open on the screen being left, then go. The target
      // surface is NOT opened here: the screen that owns it has not mounted yet,
      // so its listener does not exist and the event would be dispatched into
      // nothing. Worse, applySurface would record the surface as applied, and
      // the re-run below — the one that happens once we have actually arrived —
      // would see prev === next and do nothing at all.
      //
      // That is exactly what stopped the closing step opening the composer: it
      // is the only step that changes route AND surface at once, so it was the
      // only one that asked a screen to do something before that screen existed.
      applySurface(null);
      navigate(step.route, step.tab ? { state: { guideTab: step.tab } } : undefined);
      return;
    }

    // Arrived. Now the owning screen is mounted and listening.
    applySurface(step.surface ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id, location.pathname]);

  /**
   * Store a measured rect, but only when it differs from the one on screen.
   *
   * getBoundingClientRect() hands back a fresh object on every call, so setting
   * it unconditionally guarantees a re-render even when nothing moved. Sub-pixel
   * jitter is ignored too: a half-pixel change is invisible and re-rendering for
   * it is pure cost.
   */
  const commitRect = useCallback((rect: DOMRect) => {
    setAnchorRect(prev => {
      if (
        prev
        && Math.abs(prev.top - rect.top) < 0.5
        && Math.abs(prev.left - rect.left) < 0.5
        && Math.abs(prev.width - rect.width) < 0.5
        && Math.abs(prev.height - rect.height) < 0.5
      ) {
        return prev;
      }
      return rect;
    });
  }, []);

  /**
   * Identifies the screen a spotlight was measured against.
   *
   * Includes the LIVE pathname, not just the step's declared route: a rect is
   * only valid for the screen it was measured on, and the route can change
   * under a step. Keying off the step's own route alone meant such a change was
   * invisible here and the stale cut-out survived it.
   */
  const screenKey = `${location.pathname}|${step?.surface ?? ''}`;
  const prevScreenRef = useRef<string | null>(null);

  // ── Find and measure the anchor ────────────────────────────────────────────
  useEffect(() => {
    if (!step) return;

    clearTimers();

    // Keep the old spotlight when staying on the SAME screen, drop it when
    // moving to a different one.
    //
    // Keeping it is what stops the flash between neighbouring steps: clearing
    // unconditionally left the mask with no cut-out, so the viewport went flat
    // black for the whole settle-and-poll window on every Next tap.
    //
    // But a spotlight belongs to the screen it was measured on. Carrying it
    // across a navigation parks a cut-out over a loading screen, highlighting
    // empty space where that element no longer is. So on a screen change it
    // goes, and the bubble carries the step on its own until the new anchor is
    // found.
    const screenChanged =
      prevScreenRef.current !== null && prevScreenRef.current !== screenKey;
    prevScreenRef.current = screenKey;
    if (screenChanged) {
      setAnchorRect(null);
      // The step goes with the highlight. A bubble left sitting over a loading
      // screen is describing something that is not there yet.
      setTransitioning(true);
    }

    // An untargeted step (the closing one) needs no anchor and no waiting.
    if (!step.anchor) {
      setAnchorRect(null);
      setResolving(false);
      setTransitioning(false);
      return;
    }

    setResolving(true);
    releasedRef.current = false;
    const deadline = Date.now() + ANCHOR_TIMEOUT_MS;

    const hunt = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
      const rect = el?.getBoundingClientRect();

      // A zero-size rect means present but not laid out yet — keep waiting
      // rather than spotlighting a point.
      if (rect && rect.width > 0 && rect.height > 0) {
        // Bring it into view before spotlighting it. The tour navigates to the
        // right SCREEN but did nothing about where the target sat on that
        // screen, so an anchor below the fold — the tribes grid on Explore is
        // the obvious one — got a spotlight and a bubble positioned over part of
        // the page the reader could not see, and the screen simply did not move
        // with the step.
        //
        // No re-measure is scheduled here on purpose: scrolling fires the
        // capture-phase listener above, which already re-measures on an
        // animation frame and commits only when the rect actually moves, so the
        // cut-out follows the element down the page by itself.
        // ONLY when it is not already on screen.
        //
        // This used to centre every anchor unconditionally, which moved targets
        // that were perfectly visible already — the composer sits at the top of
        // the feed, and centring it scrolled the page so the first step
        // highlighted it somewhere it never normally appears. Scrolling exists
        // for anchors below the fold, like the tribes grid; an anchor that fits
        // on screen should be spotlit exactly where the reader already sees it.
        //
        // The margin keeps a partly-clipped anchor from counting as visible.
        const EDGE = 24;
        const fullyVisible = rect.top >= EDGE && rect.bottom <= window.innerHeight - EDGE;
        if (!fullyVisible && el && typeof el.scrollIntoView === 'function') {
          try {
            el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          } catch {
            el.scrollIntoView();
          }
        }
        commitRect(rect);
        setResolving(false);
        setTransitioning(false);

        // Then keep measuring for a short while.
        //
        // Screens are animated in by ScreenTransitionProvider, so the very first
        // rect after an anchor appears is a MID-TRANSITION position — the
        // element is still sliding. Committing that once and stopping left the
        // cut-out wherever the element happened to be part-way through, which is
        // why a target could end up unhighlighted with the hole sitting beside
        // it. Images and fonts finishing their load move things too.
        //
        // The scroll/resize listener could not cover this: a CSS transition
        // fires neither event. commitRect ignores anything that has not actually
        // moved, so this is a handful of no-op measurements in the common case.
        const settleUntil = Date.now() + ANCHOR_SETTLE_MS;
        const settle = () => {
          const live = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
          const r = live?.getBoundingClientRect();
          if (r && r.width > 0 && r.height > 0) commitRect(r);
          if (Date.now() < settleUntil) {
            settleFrameRef.current = requestAnimationFrame(settle);
          }
        };
        settleFrameRef.current = requestAnimationFrame(settle);
        return;
      }

      if (Date.now() >= deadline) {
        // Past the deadline the step is SHOWN, but the hunt does not stop.
        //
        // A deadline is the right way to decide when to stop making the reader
        // wait. It is the wrong way to decide that an element will never exist.
        // The swipe deck is the case that proves it: the step that opens it is
        // the first one to mount that screen, and its action row is gated on
        // `!loading && currentUser`, so the buttons simply are not in the DOM
        // until profiles have been fetched. Giving up at the deadline meant that
        // step never got a spotlight while the one after it — same row, a few
        // seconds later — always did.
        //
        // So the bubble is released immediately and a slower poll keeps running
        // for as long as this step is on screen, upgrading to a spotlight the
        // moment the anchor turns up. A step whose anchor genuinely never
        // appears (an empty deck) costs one cheap querySelector every 400ms and
        // behaves exactly as before.
        if (!releasedRef.current) {
          releasedRef.current = true;
          console.warn(`[Guide] anchor "${step.anchor}" not ready; showing step "${step.id}" and still watching.`);
          setAnchorRect(null);
          setResolving(false);
          // Shown even without a spotlight once the wait is over: the step is
          // still worth reading, and holding it back indefinitely would strand
          // the reader on a screen whose anchor may never arrive.
          setTransitioning(false);
        }
        timersRef.current.push(setTimeout(hunt, ANCHOR_LATE_POLL_MS));
        return;
      }


      timersRef.current.push(setTimeout(hunt, ANCHOR_POLL_MS));
    };

    // Let the navigation above paint before hunting, so the first look is not
    // guaranteed to miss.
    timersRef.current.push(setTimeout(hunt, NAV_SETTLE_MS));

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id, step?.anchor, location.pathname]);

  // Keep the cut-out on the element through scrolling and rotation.
  //
  // Two things here exist to stop the overlay pulsing.
  //
  // The listener is registered in the CAPTURE phase, because scroll does not
  // bubble and the element that actually scrolls is the feed container, not the
  // window. That means it fires for every scrolling element in the app. Each
  // call used to setAnchorRect(getBoundingClientRect()), and that returns a NEW
  // object every time, so React re-rendered on every scroll event no matter
  // whether anything had moved — redrawing a full-viewport SVG mask and an 18px
  // blurred box-shadow at scroll frequency. That was the pulsing.
  //
  // So: measurements are coalesced into one animation frame, and commitRect
  // compares the numbers and returns without touching state when the rect has
  // not actually changed.
  useEffect(() => {
    if (!active || !step?.anchor) return;
    let frame = 0;
    const remeasure = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
        const rect = el?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0) commitRect(rect);
      });
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [active, step?.anchor, commitRect]);

  useEffect(() => clearTimers, [clearTimers]);

  const value = useMemo<GuideApi>(() => ({
    active,
    visible,
    transitioning,
    stepIndex,
    anchorRect,
    resolving,
    start,
    next: () => advanceRef.current(1),
    back: () => advanceRef.current(-1),
    skip,
  }), [active, visible, transitioning, stepIndex, anchorRect, resolving, start, skip]);

  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}

/**
 * Outside a provider this reports an inert guide, so a component can ask
 * "is the tour running" without caring whether the provider is mounted.
 */
export function useGuide(): GuideApi {
  const ctx = useContext(GuideContext);
  return ctx ?? {
    active: false,
    visible: false,
    transitioning: false,
    stepIndex: -1,
    anchorRect: null,
    resolving: false,
    start: () => {},
    next: () => {},
    back: () => {},
    skip: () => {},
  };
}
